// Master-P3 Chunk C-β — Campaign Preview UI (C.6).
// Shows the 5 evidence panels per Block-A trust framework:
//   [Schema ✓] | [Model: Stub|Real] | [Business review: pending|passed]
// Never a single green tick that suggests "real AI produced this in prod".
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../../lib/api";
import BulkRegenerateModal from "../../components/app/BulkRegenerateModal";
import VideoPreviewWidget, {
  VideoPreviewSkeleton, VideoPreviewEmpty,
} from "../../components/app/VideoPreviewWidget";

const ARTIFACT_TYPES = [
  "strategy", "ad_copy", "creative_brief", "image", "video",
  "lp_draft", "quality_report", "campaign_preview",
];

function ModeChip({ mode }) {
  const isReal = mode && mode.startsWith("real_");
  const bg = isReal ? "#0e4429" : "#3a3a3a";
  const label = isReal ? `Model: Real (${mode})` : "Model: Stub";
  return (
    <span data-testid={`mode-chip-${mode || "stub"}`}
      style={{ padding: "2px 8px", borderRadius: 12, background: bg,
        color: "#fff", fontSize: 12, marginRight: 6 }}>
      {label}
    </span>
  );
}

function SchemaChip({ ok }) {
  return (
    <span data-testid={`schema-chip-${ok ? "ok" : "fail"}`}
      style={{ padding: "2px 8px", borderRadius: 12,
        background: ok ? "#0e4429" : "#8b1a1a", color: "#fff",
        fontSize: 12, marginRight: 6 }}>
      Schema {ok ? "✓" : "✗"}
    </span>
  );
}

function BusinessReviewChip({ state }) {
  const bg = state === "passed" ? "#0e4429"
    : state === "failed" ? "#8b1a1a" : "#5a4a10";
  return (
    <span data-testid={`business-review-chip-${state}`}
      style={{ padding: "2px 8px", borderRadius: 12, background: bg,
        color: "#fff", fontSize: 12, marginRight: 6 }}>
      Business review: {state}
    </span>
  );
}

function VersionChip({ briefVersion }) {
  return (
    <span data-testid="version-chip"
      style={{ padding: "2px 8px", borderRadius: 12,
        background: "#1a3a5c", color: "#fff", fontSize: 12 }}>
      Brief v{briefVersion}
    </span>
  );
}

function CostChip({ costEur, remainingEur, mode }) {
  if (!mode || !mode.startsWith("real_")) {
    return (
      <span data-testid="cost-chip-stub"
        style={{ fontSize: 12, color: "#888" }}>
        [stub — no cost]
      </span>
    );
  }
  return (
    <span data-testid="cost-chip-real"
      style={{ fontSize: 12, color: "#e8b400" }}>
      cost: {costEur.toFixed(2)}€ · remaining: {remainingEur.toFixed(2)}€
    </span>
  );
}

function ArtifactCard({ artifact }) {
  const s = artifact.structured_content || {};
  const isSuperseded = artifact.status === "superseded";
  return (
    <div data-testid={`artifact-${artifact.artifact_type}-${artifact.id}`}
      style={{ border: "1px solid #333", borderRadius: 8,
        padding: 12, marginBottom: 10,
        opacity: isSuperseded ? 0.5 : 1 }}>
      <div style={{ marginBottom: 6 }}>
        <SchemaChip ok={artifact.status !== "failed"} />
        <ModeChip mode={artifact.execution_mode} />
        <BusinessReviewChip
          state={artifact.status === "failed" ? "failed" : "pending"} />
        {isSuperseded && (
          <span data-testid="superseded-tag"
            style={{ padding: "2px 8px", borderRadius: 12,
              background: "#8b1a1a", color: "#fff", fontSize: 12 }}>
            SUPERSEDED
          </span>
        )}
      </div>
      <div style={{ fontSize: 14, fontFamily: "monospace",
          background: "#0a0a0a", padding: 8, borderRadius: 6,
          maxHeight: 160, overflow: "auto" }}>
        <pre style={{ margin: 0 }}>{JSON.stringify(s, null, 2)}</pre>
      </div>
    </div>
  );
}

export default function CampaignPreview() {
  const { brief_id } = useParams();
  const [runId, setRunId] = useState("");
  const [briefVersion, setBriefVersion] = useState(1);
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regenOpen, setRegenOpen] = useState(false);

  const loadArtifacts = () => {
    setLoading(true);
    return api.get("/campaign-artifacts", { params: { brief_id } })
      .then(r => {
        const items = r.data.items || [];
        setArtifacts(items);
        if (items.length) {
          setRunId(items[0].run_id);
          setBriefVersion(items[0].brief_version);
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadArtifacts(); }, [brief_id]);

  const grouped = useMemo(() => {
    const g = {};
    for (const t of ARTIFACT_TYPES) g[t] = [];
    for (const a of artifacts) {
      if (!g[a.artifact_type]) g[a.artifact_type] = [];
      g[a.artifact_type].push(a);
    }
    return g;
  }, [artifacts]);

  const supersededCount = artifacts.filter(a => a.status === "superseded")
    .length;

  return (
    <div data-testid="campaign-preview" style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: 14 }}>
        <h2 data-testid="campaign-preview-title">Campaign Preview</h2>
        <VersionChip briefVersion={briefVersion} />
      </div>

      {supersededCount > 0 && (
        <div data-testid="supersede-warning"
          style={{ background: "#3a2a10", color: "#e8b400",
            padding: 10, borderRadius: 6, marginBottom: 14,
            display: "flex", alignItems: "center",
            justifyContent: "space-between", gap: 12 }}>
          <span>
            {supersededCount} outdated artifact(s) — brief has newer version.
            Use per-step revalidate or regenerate to update.
          </span>
          <button data-testid="bulk-regen-open-btn"
                    disabled={!runId}
                    onClick={() => setRegenOpen(true)}
                    style={{ background: "#0e4429", color: "#fff",
                              border: "1px solid #1a6f47",
                              padding: "6px 12px", borderRadius: 4,
                              cursor: runId ? "pointer" : "not-allowed" }}>
            Alle regenerieren
          </button>
        </div>
      )}

      <BulkRegenerateModal open={regenOpen} runId={runId}
                              briefVersion={briefVersion}
                              onClose={() => setRegenOpen(false)}
                              onDone={() => loadArtifacts()} />

      {error && (
        <div data-testid="campaign-preview-error"
          style={{ background: "#8b1a1a", color: "#fff",
            padding: 10, borderRadius: 6 }}>
          {String(error)}
        </div>
      )}

      <section data-testid="ad-copies-panel">
        <h3>Ad Copies</h3>
        {grouped.ad_copy.length === 0
          ? <p data-testid="empty-ad-copy">No ad copies yet.</p>
          : grouped.ad_copy.map(a =>
              <ArtifactCard key={a.id} artifact={a} />)}
      </section>

      <section data-testid="creative-renders-panel">
        <h3>Creative Renders</h3>
        {grouped.image.length === 0
          ? <p data-testid="empty-images">No renders yet.</p>
          : grouped.image.map(a => (
            <div key={a.id} data-testid={`render-card-${a.id}`}>
              <ArtifactCard artifact={a} />
              <CostChip
                costEur={parseFloat(a.structured_content?.cost_eur || 0)}
                remainingEur={parseFloat(
                  a.structured_content?.budget_remaining_eur || 0)}
                mode={a.execution_mode} />
            </div>))}
      </section>

      <section data-testid="video-preview-panel">
        <h3>Video Preview</h3>
        {loading && grouped.video.length === 0
          ? <VideoPreviewSkeleton />
          : grouped.video.length === 0
            ? <VideoPreviewEmpty />
            : grouped.video.map(a => (
              <VideoPreviewWidget key={a.id} artifact={a} />))}
      </section>

      <section data-testid="lp-draft-panel">
        <h3>LP Draft Preview</h3>
        {grouped.lp_draft.length === 0
          ? <p data-testid="empty-lp-draft">No LP draft yet.</p>
          : grouped.lp_draft.map(a => {
            const url = a.structured_content?.preview_url;
            return (
              <div key={a.id}>
                <ArtifactCard artifact={a} />
                {url && (
                  <iframe data-testid="lp-preview-iframe" title="LP Preview"
                    src={url} style={{ width: "100%", height: 320,
                      border: "1px solid #333", borderRadius: 6 }} />
                )}
              </div>);
          })}
      </section>

      <section data-testid="quality-check-panel">
        <h3>Quality Check</h3>
        {grouped.quality_report.length === 0
          ? <p data-testid="empty-quality">No quality report yet.</p>
          : grouped.quality_report.map(a => (
            <div key={a.id}>
              <ArtifactCard artifact={a} />
              <ul>
                {(a.structured_content?.issues || []).map((iss, i) =>
                  <li key={i} data-testid={`qc-issue-${i}`}>{iss}</li>)}
              </ul>
            </div>))}
      </section>

      <section data-testid="cost-summary-panel">
        <h3>Cost Summary</h3>
        <p>
          Spent so far — read from budget_ledger for run {runId || "—"}.
        </p>
        <p data-testid="cost-summary-note" style={{ color: "#888" }}>
          Full budget ledger view will surface here in Chunk C-γ once a
          real budget frame is approved. Currently: stub-mode only, no
          real cost has been billed.
        </p>
      </section>
    </div>
  );
}
